import React, { useState, useEffect } from "react";
import BookDataService from "../services/BookService";
import { GetApp as DownloadIcon } from "@mui/icons-material";
import "./IBC.css";

const IBC = () => {
  const [ibcData, setIbcData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getLanguages = (submission) => {
    if (Array.isArray(submission.language) && submission.language.length > 0) {
      return submission.language;
    }
    try {
      if (submission.languageJson) {
        const parsed = JSON.parse(submission.languageJson);
        return Array.isArray(parsed) ? parsed : [];
      }
    } catch (e) {
      // ignore parse errors and fall back to empty array
    }
    return [];
  };

  const shouldShowSpecifiedVoiceId = (submission) => {
    const selection = (submission.voiceSelection || "").toLowerCase();
    if (selection === "auto-voice" || selection === "audio-voice") {
      return false;
    }
    return Boolean(submission.selectedVoiceId);
  };

  useEffect(() => {
    const fetchIBCData = async () => {
      try {
        setLoading(true);
        const response = await BookDataService.getAllIBCData();
        setIbcData(response.data);
      } catch (err) {
        setError("Failed to load IBC data");
        console.error("Error fetching IBC data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchIBCData();
  }, []);

  const formatDate = (dateArray) => {
    if (!dateArray || dateArray.length < 6) return "Unknown date";
    const [year, month, day, hour, minute, second] = dateArray;
    return new Date(
      year,
      month - 1,
      day,
      hour,
      minute,
      second
    ).toLocaleDateString();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileTypeIcon = (fileType) => {
    switch (fileType) {
      case "IMAGE":
        return "🖼️";
      case "VIDEO":
        return "🎥";
      case "AUDIO":
        return "🎵";
      default:
        return "📄";
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "headshot":
        return "👤";
      case "logo":
        return "🏢";
      case "review-video":
        return "📹";
      default:
        return "📎";
    }
  };

  if (loading) {
    return (
      <div className="ibc-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading submissions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ibc-container">
        <div className="error-message">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="ibc-container">
      <div className="ibc-header">
        <h1>Smart Reach Submissions</h1>
        <p>View all submitted business cards and information</p>
      </div>

      {ibcData.length === 0 ? (
        <div className="no-data">
          <p>No submissions found</p>
        </div>
      ) : (
        <div className="submissions-grid">
          {ibcData.map((submission) => (
            <div key={submission.id} className="submission-card">
              {/* Header with basic info */}
              <div className="card-header">
                <div className="business-info">
                  <h3>{submission.name}</h3>
                  <p className="email">{submission.email}</p>
                </div>
              </div>

              {/* Personal Contact Information */}
              <div className="contact-section">
                <h4>Personal Contact Information</h4>
                <div className="contact-grid">
                  <div className="contact-item">
                    <span className="label">Name:</span>
                    <span className="value">{submission.name}</span>
                  </div>
                  {submission.phone && (
                    <div className="contact-item">
                      <span className="label">Phone:</span>
                      <span className="value">{submission.phone}</span>
                    </div>
                  )}
                  {submission.email && (
                    <div className="contact-item">
                      <span className="label">Email:</span>
                      <span className="value">{submission.email}</span>
                    </div>
                  )}
                  {submission.address && (
                    <div className="contact-item">
                      <span className="label">Address:</span>
                      <span className="value">{submission.address}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Business Information */}
              <div className="business-section">
                <h4>Business Information</h4>
                <div className="business-grid">
                  {submission.businessName && (
                    <div className="business-item">
                      <span className="label">Business Name:</span>
                      <span className="value">{submission.businessName}</span>
                    </div>
                  )}
                  {submission.businessPhone && (
                    <div className="business-item">
                      <span className="label">Business Phone:</span>
                      <span className="value">{submission.businessPhone}</span>
                    </div>
                  )}
                  {submission.fax && (
                    <div className="business-item">
                      <span className="label">Fax:</span>
                      <span className="value">{submission.fax}</span>
                    </div>
                  )}
                  {submission.businessAddress && (
                    <div className="business-item">
                      <span className="label">Business Address:</span>
                      <span className="value">
                        {submission.businessAddress}
                      </span>
                    </div>
                  )}
                  {submission.website && (
                    <div className="business-item">
                      <span className="label">Website:</span>
                      <a
                        href={`https://${submission.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="value link"
                      >
                        {submission.website}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Project Details */}
              {(submission.projectType || submission.additionalComments) && (
                <div className="project-section">
                  <h4>Project Details</h4>
                  <div className="business-grid">
                    {submission.projectType && (
                      <div className="business-item">
                        <span className="label">Project Type:</span>
                        <span className="value">{submission.projectType}</span>
                      </div>
                    )}
                    {submission.additionalComments && (
                      <div className="business-item">
                        <span className="label">Additional Comments:</span>
                        <span className="value">
                          {submission.additionalComments}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Bio */}
              {submission.bio && (
                <div className="bio-section">
                  <h4>Bio</h4>
                  <p>{submission.bio}</p>
                </div>
              )}

              {/* Social Links */}
              {submission.links && submission.links.length > 0 && (
                <div className="links-section">
                  <h4>Social Links</h4>
                  <div className="links-grid">
                    {submission.links.map((link, index) => (
                      <div key={index} className="link-item">
                        <span className="link-type">{link.type}:</span>
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="link-url"
                        >
                          {link.url}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Language Preferences */}
              {getLanguages(submission).length > 0 && (
                <div className="languages-section">
                  <h4>Language Preferences</h4>
                  <div className="links-grid">
                    {getLanguages(submission).map((lang, index) => (
                      <div key={index} className="link-item">
                        <span className="link-type">Language:</span>
                        <span className="value">{lang}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Custom Colors */}
              {submission.customColors &&
                submission.customColors.length > 0 && (
                  <div className="colors-section">
                    <h4>Brand Colors</h4>
                    <div className="colors-grid">
                      {submission.customColors.map((color, index) => (
                        <div key={index} className="color-item">
                          <div
                            className="color-swatch"
                            style={{ backgroundColor: color }}
                            title={color}
                          ></div>
                          <span className="color-code">{color}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Attachments */}
              {submission.attachments && submission.attachments.length > 0 && (
                <div className="attachments-section">
                  <h4>Attachments</h4>
                  <div className="attachments-grid">
                    {submission.attachments.map((attachment) => (
                      <div key={attachment.id} className="attachment-item">
                        <div className="attachment-header">
                          <span className="file-icon">
                            {getFileTypeIcon(attachment.fileType)}
                          </span>
                          <span className="category-icon">
                            {getCategoryIcon(attachment.category)}
                          </span>
                          <span className="file-name">
                            {attachment.fileName}
                          </span>
                        </div>
                        <div className="attachment-details">
                          <span className="category">
                            {attachment.category}
                          </span>
                          <span className="file-size">
                            {formatFileSize(attachment.sizeBytes)}
                          </span>
                          <span className="upload-date">
                            {formatDate(attachment.uploadedAt)}
                          </span>
                          <a
                            href={`https://d9fedpdsh4r16.cloudfront.net/${attachment.s3Key.replace(
                              "dpi-assets/",
                              ""
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="download-link"
                            title="Download file"
                          >
                            <DownloadIcon fontSize="small" />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Settings */}
              <div className="settings-section">
                <h4>Settings</h4>
                <div className="settings-grid">
                  <div className="setting-item">
                    <span className="label">Voice Selection:</span>
                    <span className="value">
                      {submission.voiceSelection || "Not specified"}
                    </span>
                  </div>
                  <div className="setting-item">
                    <span className="label">Voice ID:</span>
                    <span className="value voice-id">
                      {shouldShowSpecifiedVoiceId(submission)
                        ? submission.selectedVoiceId
                        : "User did not specify"}
                    </span>
                  </div>
                  <div className="setting-item">
                    <span className="label">Use Brand Colors:</span>
                    <span
                      className={`value ${
                        submission.useBrandColors ? "enabled" : "disabled"
                      }`}
                    >
                      {submission.useBrandColors ? "Yes" : "No"}
                    </span>
                  </div>
                  <div className="setting-item">
                    <span className="label">
                      Permission for edits and modifications:
                    </span>
                    <span className="value">
                      {submission.consent
                        ? "Edit and enhance my texts and materials as needed"
                        : "Use my texts and materials exactly as submitted"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default IBC;
